/**
 * Shared document-type icon resolver for the chat citation UI.
 *
 * Renders a colored SVG document-type icon (the generated badge-style icons in
 * ../../assets/images/doc-type-icons) for a given source_type / title. Each icon
 * is loaded as an <img> so the duplicate gradient/clip ids across the SVG files
 * stay isolated per image (no global id collisions).
 *
 * The `size` prop scales the icon independently (default 28px, large enough to
 * read the file-type label on the badge); pass a different size as needed.
 */
import React from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import { Box } from '@mui/material';

// Generated badge-style document-type icons (Vite resolves these to URLs).
import pdfIcon from '../../assets/images/doc-type-icons/pdf.svg';
import docIcon from '../../assets/images/doc-type-icons/doc.svg';
import docxIcon from '../../assets/images/doc-type-icons/docx.svg';
import xlsxIcon from '../../assets/images/doc-type-icons/xlsx.svg';
import txtIcon from '../../assets/images/doc-type-icons/txt.svg';
import mdIcon from '../../assets/images/doc-type-icons/md.svg';
import pptIcon from '../../assets/images/doc-type-icons/ppt.svg';
import imageIcon from '../../assets/images/doc-type-icons/image.svg';
import urlIcon from '../../assets/images/doc-type-icons/url.svg';
import apiIcon from '../../assets/images/doc-type-icons/api.svg';
import databaseIcon from '../../assets/images/doc-type-icons/database.svg';
import dataSourceIcon from '../../assets/images/doc-type-icons/data-source.svg';
import webhookIcon from '../../assets/images/doc-type-icons/webhook.svg';

// source_type values that already name a specific category — these must win
// over file_type (e.g. a URL document is stored with file_type "text", which
// would otherwise mask it as a plain text file).
const SPECIFIC_SOURCE_TYPES = new Set([
  'pdf', 'docx', 'word', 'pptx', 'powerpoint', 'xlsx', 'excel', 'txt', 'manual',
  'png', 'jpg', 'jpeg', 'gif', 'bmp', 'tiff', 'webp', 'image',
  'md', 'markdown', 'url', 'web', 'faq', 'sharepoint', 'msgraph',
  'api', 'database', 'data-source', 'webhook', 'sap',
]);

/**
 * Resolve the type hint to feed the icon/label resolvers.
 *
 * Prefer ``source_type`` when it already names a specific category (so a URL
 * document keeps its web icon); only fall back to ``file_type`` to disambiguate
 * a generic source_type like "file" / "document" (so an uploaded image gets the
 * image icon instead of the generic fallback).
 *
 * When the source_type is generic but an external http(s) ``url`` is present,
 * treat it as a web source. This also corrects messages persisted before the
 * backend promoted the real "url" source_type (their stored type is the generic
 * "document" but they still carry the original web URL).
 */
export function resolveTypeHint(
  sourceType: string | null | undefined,
  fileType: string | null | undefined,
  url?: string | null
): string | null | undefined {
  if (sourceType && SPECIFIC_SOURCE_TYPES.has(sourceType.toLowerCase())) {
    return sourceType;
  }
  if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
    return 'url';
  }
  return fileType ?? sourceType;
}

/**
 * Short human-readable source type label.
 * Exported for reuse in type-badge components.
 */
export function sourceTypeLabel(
  sourceType: string | null | undefined,
  titleFallback?: string | null
): string {
  switch (sourceType) {
    case 'pdf':
      return 'PDF';
    case 'docx':
    case 'word':
      return 'Word';
    case 'pptx':
    case 'powerpoint':
      return 'PPTX';
    case 'xlsx':
    case 'excel':
      return 'Excel';
    case 'md':
    case 'markdown':
      return 'Markdown';
    case 'txt':
    case 'manual':
      // Manual = free-text ("Textinhalt") pasted by the user; it is plain text,
      // so it shares the Text badge rather than the generic data-source icon.
      return 'Text';
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'bmp':
    case 'tiff':
    case 'webp':
    case 'image':
      return 'Image';
    case 'url':
    case 'web':
      return 'Web';
    case 'faq':
      return 'FAQ';
    case 'sharepoint':
    case 'msgraph':
      return 'SharePoint';
    default: {
      const title = titleFallback ?? '';
      const ext = title.split('.').pop()?.toLowerCase() ?? '';
      if (ext === 'pdf') { return 'PDF'; }
      if (ext === 'docx' || ext === 'doc') { return 'Word'; }
      if (ext === 'pptx' || ext === 'ppt') { return 'PPTX'; }
      if (ext === 'xlsx' || ext === 'xls') { return 'Excel'; }
      if (ext === 'txt') { return 'Text'; }
      if (ext === 'md' || ext === 'markdown') { return 'Markdown'; }
      if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'tiff', 'webp'].includes(ext)) { return 'Image'; }
      if (title.startsWith('http://') || title.startsWith('https://')) { return 'Web'; }
      if (sourceType && /^[a-z]{2,6}$/i.test(sourceType)) { return sourceType.toUpperCase().slice(0, 6); }
      return 'Doc';
    }
  }
}

/**
 * Type-specific foreground color for the document icon. Mirrors the fill colors
 * baked into the generated SVG badges, for use in non-icon contexts (e.g. a
 * colored left border on a quote block, or text accents).
 */
export function documentTypeColor(
  sourceType: string | null | undefined,
  title?: string | null
): string {
  const label = sourceTypeLabel(sourceType, title);
  switch (label) {
    case 'PDF':
      return '#f44343';
    case 'Word':
      return '#2b6ff7';
    case 'PPTX':
      return '#f28c28';
    case 'Excel':
      return '#1f9d55';
    case 'Web':
    case 'SharePoint':
      return '#01696f';
    case 'Text':
      return '#bfc5cc';
    case 'FAQ':
      return '#006494';
    case 'Image':
      // Magenta, matching image.svg — distinct from the purple data-source.
      return '#d6336c';
    default:
      return '#7a39bb';
  }
}

/**
 * Resolve a source_type / title to the URL of its generated SVG badge icon.
 */
function documentTypeIconSrc(
  sourceType: string | null | undefined,
  title?: string | null
): string {
  // Non-file source categories first (these have dedicated badge art).
  switch (sourceType) {
    case 'api':
      return apiIcon;
    case 'database':
    case 'data-source':
      return databaseIcon;
    case 'webhook':
      return webhookIcon;
    default:
      break;
  }

  const label = sourceTypeLabel(sourceType, title);
  switch (label) {
    case 'PDF':
      return pdfIcon;
    case 'Word':
      // Prefer .docx art; fall back to .doc when the title says so.
      return (title ?? '').toLowerCase().endsWith('.doc') ? docIcon : docxIcon;
    case 'PPTX':
      return pptIcon;
    case 'Excel':
      return xlsxIcon;
    case 'Text':
      return txtIcon;
    case 'Markdown':
      return mdIcon;
    case 'Image':
      return imageIcon;
    case 'Web':
    case 'SharePoint':
    case 'FAQ':
      return urlIcon;
    default:
      return dataSourceIcon;
  }
}

interface DocumentTypeIconProps {
  sourceType: string | null | undefined;
  title?: string | null;
  /** Rendered icon size in px (square). Default 28. */
  size?: number;
  /** Additional MUI sx overrides on the <img> wrapper. */
  sx?: SxProps<Theme>;
}

/**
 * Return a colored SVG document-type badge icon for a given document type,
 * rendered as an <img> at the requested square size. Default size is 28px
 * (Sources header) — large enough to read the file-type label.
 */
export function documentTypeIcon(
  sourceType: string | null | undefined,
  title?: string | null,
  size = 28,
  sx?: SxProps<Theme>
): React.ReactElement {
  const src = documentTypeIconSrc(sourceType, title);
  const label = sourceTypeLabel(sourceType, title);
  return (
    <Box
      component="img"
      src={src}
      alt={`${label} icon`}
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        display: 'inline-block',
        objectFit: 'contain',
        ...(sx as object),
      }}
    />
  );
}

/**
 * React component wrapper for documentTypeIcon, suitable for JSX usage
 * in contexts that need a component (not a call).
 */
export const DocumentTypeIcon: React.FC<DocumentTypeIconProps> = ({
  sourceType,
  title,
  size = 28,
  sx,
}) => documentTypeIcon(sourceType, title, size, sx);
